import { Layout as AntLayout, Button } from 'antd'
import {
  HomeOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../utils/redux/store'
import './MainLayout.scss'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { attemptGetFavs } from '../utils/redux/favsSlice'
import UploadedImage from './PlaylistImage'
import { useAudioPlayer } from '../contexts/AudioPlayerContext'
import { THEME_ID } from '../utils/redux/appSlice'
import Logo from './Logo'

const { Sider, Content } = AntLayout

function playlistImageUrl(image: string) {
  const base = String(process.env.REACT_APP_UPLOADED_BASE_URL || '').replace(/\/$/, '')
  const clean = String(image || '').replace(/^\/+/, '')
  return base && clean ? `${base}/playlists/${clean}` : ''
}

type ResolvedTheme = 'dark' | 'light'

function getResolvedTheme(themeId: THEME_ID): ResolvedTheme {
  if (themeId === THEME_ID.LIGHT) return 'light'
  if (themeId === THEME_ID.DARK) return 'dark'
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark'
}

/**
 * ✅ Кастомный скроллбар (всегда видимый) для macOS:
 * Нативный scrollbar на macOS может быть скрыт системной настройкой.
 * Поэтому для liked-section рисуем свой (нативный скрываем только там).
 */
function ScrollArea({
  children,
  ariaLabel,
}: {
  children: ReactNode
  ariaLabel?: string
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [thumb, setThumb] = useState<{ h: number; y: number; hidden: boolean }>({
    h: 28,
    y: 0,
    hidden: true,
  })

  const sync = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const ch = el.clientHeight
    const sh = el.scrollHeight
    const st = el.scrollTop

    if (sh <= ch + 1) {
      setThumb((p) => ({ ...p, y: 0, hidden: true }))
      return
    }

    const minThumb = 28
    const h = Math.max(minThumb, Math.round((ch / sh) * ch))
    const maxY = Math.max(1, ch - h)
    const y = Math.round((st / (sh - ch)) * maxY)

    setThumb({ h, y, hidden: false })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const onScroll = () => sync()
    el.addEventListener('scroll', onScroll, { passive: true })
    sync()

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => sync())
      ro.observe(el)
    }

    window.addEventListener('resize', sync)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro?.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [sync])

  const dragRef = useRef<{
    startY: number
    startScrollTop: number
    ratio: number
    pointerId: number
  } | null>(null)

  const onThumbPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    if (thumb.hidden) return

    e.preventDefault()
    e.stopPropagation()

    const ch = el.clientHeight
    const sh = el.scrollHeight
    const maxY = Math.max(1, ch - thumb.h)
    const ratio = (sh - ch) / maxY

    dragRef.current = {
      startY: e.clientY,
      startScrollTop: el.scrollTop,
      ratio,
      pointerId: e.pointerId,
    }

    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onThumbPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    const drag = dragRef.current
    if (!el || !drag) return
    if (e.pointerId !== drag.pointerId) return

    e.preventDefault()

    const dy = e.clientY - drag.startY
    el.scrollTop = drag.startScrollTop + dy * drag.ratio
  }

  const onThumbPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    if (e.pointerId !== drag.pointerId) return
    dragRef.current = null
  }

  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    if (thumb.hidden) return

    const rect = e.currentTarget.getBoundingClientRect()
    const ch = el.clientHeight
    const sh = el.scrollHeight
    const maxY = Math.max(1, ch - thumb.h)

    const clickY = e.clientY - rect.top
    const newY = Math.min(Math.max(0, clickY - thumb.h / 2), maxY)
    const ratio = (sh - ch) / maxY

    el.scrollTop = newY * ratio
  }

  return (
    <div className="likedScrollWrap">
      <div ref={scrollRef} className="liked-section" aria-label={ariaLabel}>
        {children}
      </div>

      <div
        className={`likedScrollBar ${thumb.hidden ? 'isHidden' : ''}`}
        aria-hidden="true"
        onPointerDown={onTrackPointerDown}
      >
        <div
          className="likedScrollThumb"
          style={{ height: thumb.h, transform: `translateY(${thumb.y}px)` }}
          onPointerDown={onThumbPointerDown}
          onPointerMove={onThumbPointerMove}
          onPointerUp={onThumbPointerUp}
        />
      </div>
    </div>
  )
}

export default function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation('common')
  const { playQueue } = useAudioPlayer()

  const [isMobile, setIsMobile] = useState(false)
  const mobileDockRef = useRef<HTMLDivElement | null>(null)

  // тема из redux
  const themeId =
    useAppSelector((state: any) => state.app?.themeId as THEME_ID) ?? THEME_ID.DARK
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    getResolvedTheme(themeId),
  )

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }

    // safari fallback
    // @ts-ignore
    mq.addListener(onChange)
    // @ts-ignore
    return () => mq.removeListener(onChange)
  }, [])

  // следим за темой (в т.ч. AS_DEVICE)
  useEffect(() => {
    setResolvedTheme(getResolvedTheme(themeId))

    if (themeId !== THEME_ID.AS_DEVICE || typeof window === 'undefined') return

    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onSchemeChange = () => setResolvedTheme(mql.matches ? 'dark' : 'light')

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onSchemeChange)
      return () => mql.removeEventListener('change', onSchemeChange)
    }

    // safari fallback
    // @ts-ignore
    mql.addListener(onSchemeChange)
    // @ts-ignore
    return () => mql.removeListener(onSchemeChange)
  }, [themeId])

  // Пишем реальную высоту mobile bottom overlay (dock + mini player) в CSS var.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement

    const setBottomOverlay = () => {
      if (!isMobile || !mobileDockRef.current) {
        root.style.setProperty('--app-bottom-overlay', '0px')
        return
      }
      const h = Math.ceil(mobileDockRef.current.getBoundingClientRect().height)
      root.style.setProperty('--app-bottom-overlay', `${h}px`)
    }

    setBottomOverlay()

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && mobileDockRef.current) {
      ro = new ResizeObserver(() => setBottomOverlay())
      ro.observe(mobileDockRef.current)
    }

    window.addEventListener('resize', setBottomOverlay)

    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', setBottomOverlay)
      root.style.setProperty('--app-bottom-overlay', '0px')
    }
  }, [isMobile])

  const { songs, playlists } = useAppSelector(
    (state: any) => state.favs ?? { songs: [], playlists: [] },
  )

  const desktopMenuItems = useMemo(
    () => [
      {
        key: '/home',
        icon: <HomeOutlined />,
        label: t('home'),
        onClick: () => navigate('/home'),
      },
      {
        key: '/orders',
        icon: <ShoppingCartOutlined />,
        label: t('orders'),
        onClick: () => navigate('/orders'),
      },
      {
        key: '/settings',
        icon: <SettingOutlined />,
        label: t('settings'),
        onClick: () => navigate('/settings'),
      },
      {
        key: '/history',
        icon: <HistoryOutlined />,
        label: t('history', { defaultValue: 'History' }),
        onClick: () => navigate('/history'),
      },
    ],
    [navigate, t],
  )

  const mobileMenuItems = useMemo(
    () => [
      {
        key: '/home',
        icon: <HomeOutlined />,
        label: t('home'),
        onClick: () => navigate('/home'),
      },
      {
        key: '/orders',
        icon: <ShoppingCartOutlined />,
        label: t('orders'),
        onClick: () => navigate('/orders'),
      },
      {
        key: '/history',
        icon: <HistoryOutlined />,
        label: t('history', { defaultValue: 'History' }),
        onClick: () => navigate('/history'),
      },
      {
        key: '/settings',
        icon: <SettingOutlined />,
        label: t('settings'),
        onClick: () => navigate('/settings'),
      },
    ],
    [navigate, t],
  )
  const selectedKey =
    [...desktopMenuItems, ...mobileMenuItems].find((item) =>
      location.pathname.startsWith(item.key),
    )?.key || '/home'

  const onClickLikedSong = (song: any) => {
    playQueue([song], 0)
  }

  const onClickLikedPlaylist = (playlist: any) => {
    const pid = String(playlist?._id || playlist?.id || '')
    if (!pid) return
    navigate(`/playlist/${pid}`)
  }

  const onKeyActivate = (fn: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }

  return (
    <AntLayout
      className={`main-layout ${resolvedTheme === 'light' ? 'is-light' : 'is-dark'}`}
      data-theme={resolvedTheme}
    >
      {!isMobile && (
        <Sider className="main-sider" width={300}>
          <div className="sider-logoWrap">
            <Logo textColor={resolvedTheme === 'light' ? 'black' : 'white'} />
          </div>

          <div className="sider-menu">
            {desktopMenuItems.map((item) => {
              const isSelected = selectedKey === item.key
              return (
                <Button
                  key={item.key}
                  type="text"
                  onClick={item.onClick}
                  block
                  className={`sider-btn ${isSelected ? 'is-selected' : ''}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Button>
              )
            })}
          </div>

          <div className="liked-title">{t('likedMusic')}</div>

          <ScrollArea ariaLabel={t('likedMusic')}>
            {songs?.map((song: any) => (
              <div
                key={song._id}
                className="liked-item"
                role="button"
                tabIndex={0}
                onClick={() => onClickLikedSong(song)}
                onKeyDown={onKeyActivate(() => onClickLikedSong(song))}
              >
                <UploadedImage
                  src={song.imageUrl}
                  fallback="/icons/heart.svg"
                  style={{
                    backgroundColor: 'var(--liked-art-bg)',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                <div className="liked-text">
                  <div className="liked-song-title">{song.title}</div>
                  <div className="liked-song-artist">{song.artist}</div>
                </div>
              </div>
            ))}
          </ScrollArea>

          <div className="liked-title">{t('likedPlaylists')}</div>

          <ScrollArea ariaLabel={t('likedPlaylists')}>
            {playlists?.map((playlist: any) => (
              <div
                key={playlist._id}
                className="liked-item"
                role="button"
                tabIndex={0}
                onClick={() => onClickLikedPlaylist(playlist)}
                onKeyDown={onKeyActivate(() => onClickLikedPlaylist(playlist))}
              >
                <UploadedImage
                  src={playlistImageUrl(playlist.image)}
                  fallback="/icons/heart.svg"
                  style={{
                    backgroundColor: 'var(--liked-art-bg)',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    marginRight: 8,
                    verticalAlign: 'middle',
                  }}
                />
                <div className="liked-text">
                  <div className="liked-song-title">{String(playlist.title ?? '')}</div>
                </div>
              </div>
            ))}
          </ScrollArea>
        </Sider>
      )}

      <Content className="main-content">
        <Outlet />

        {isMobile && (
          <div
            ref={mobileDockRef}
            className="bottomDockShell"
            aria-label={t('a11y.bottomNavigationShell')}
          >
            <div id="ga-mobile-slot" className="bbPlayerSlot" />

            <nav className="bottomDock" aria-label={t('a11y.bottomNavigation')}>
              {mobileMenuItems.map((item) => {
                const isActive = selectedKey === item.key
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`bbItem ${isActive ? 'isActive' : ''}`}
                    onClick={item.onClick}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="bbIcon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="bbLabel">{item.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        )}
      </Content>
    </AntLayout>
  )
}
